import { useState, useEffect } from "react";
import { AlertTriangle, X, LogOut, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ImpersonationBanner - Enterprise-grade admin support mode indicator
 * 
 * This component displays a persistent, non-removable red banner when an admin
 * is impersonating a user account. It shows:
 * - Clear "ADMIN SUPPORT MODE" indicator
 * - Target user info being viewed
 * - Time remaining before automatic session expiry
 * - Exit button to end impersonation
 * 
 * Security features:
 * - Cannot be dismissed or hidden
 * - Shows admin email for accountability
 * - Auto-expires based on backend timeout (30 min)
 */
const ImpersonationBanner = ({ 
  impersonationData, 
  onExitImpersonation,
  impersonationToken 
}) => {
  const [timeRemaining, setTimeRemaining] = useState("");
  const [isExiting, setIsExiting] = useState(false);

  // Calculate and update time remaining
  useEffect(() => {
    if (!impersonationData?.expires_at) return;

    const updateTimer = () => {
      const expiresAt = new Date(impersonationData.expires_at);
      const now = new Date();
      const diff = expiresAt - now;

      if (diff <= 0) {
        setTimeRemaining("EXPIRED");
        // Auto-exit when expired
        onExitImpersonation?.();
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, "0")}`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [impersonationData?.expires_at, onExitImpersonation]);

  const handleExit = async () => {
    if (isExiting) return;
    setIsExiting(true);

    try {
      const token = localStorage.getItem("hireun_session_token");
      
      await axios.post(
        `${API}/admin/end-impersonate`,
        { impersonation_session_id: impersonationData.impersonation_session_id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Impersonation session ended");
      onExitImpersonation?.();
    } catch (error) {
      console.error("Failed to end impersonation:", error);
      toast.error("Failed to end impersonation session");
      // Still exit locally even if API fails
      onExitImpersonation?.();
    } finally {
      setIsExiting(false);
    }
  };

  if (!impersonationData) return null;

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[9999] bg-red-600 text-white shadow-lg"
      data-testid="impersonation-banner"
      style={{ 
        // Ensure it cannot be hidden via CSS overrides
        display: "flex !important",
        visibility: "visible !important",
        opacity: "1 !important"
      }}
    >
      <div className="max-w-7xl mx-auto px-4 py-2 w-full">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          {/* Left: Warning + Status */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-red-700 px-3 py-1 rounded-full">
              <AlertTriangle className="w-4 h-4 animate-pulse" />
              <span className="font-bold text-sm uppercase tracking-wider">
                Admin Support Mode
              </span>
            </div>
            
            <div className="hidden sm:flex items-center gap-2 text-red-100 text-sm">
              <span>Viewing as:</span>
              <span className="font-semibold text-white">
                {impersonationData.target_user?.email || impersonationData.target_user?.name || "User"}
              </span>
            </div>
          </div>

          {/* Center: Admin Info */}
          <div className="hidden md:flex items-center gap-2 text-red-100 text-xs">
            <span>Admin:</span>
            <span className="font-medium text-white">{impersonationData.admin_email}</span>
          </div>

          {/* Right: Timer + Exit */}
          <div className="flex items-center gap-3">
            {/* Time Remaining */}
            <div className="flex items-center gap-1 bg-red-700/50 px-2 py-1 rounded text-sm">
              <Clock className="w-3 h-3" />
              <span className="font-mono font-bold">{timeRemaining}</span>
            </div>

            {/* Exit Button - Always visible, cannot be hidden */}
            <Button
              onClick={handleExit}
              disabled={isExiting}
              variant="outline"
              size="sm"
              className="bg-white text-red-600 hover:bg-red-50 border-white font-semibold"
              data-testid="exit-impersonation-btn"
            >
              {isExiting ? (
                <>
                  <div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin mr-2" />
                  Exiting...
                </>
              ) : (
                <>
                  <LogOut className="w-3 h-3 mr-2" />
                  Exit Support Mode
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Mobile: Target user info */}
        <div className="sm:hidden mt-2 text-xs text-red-100">
          Viewing as: <span className="font-semibold text-white">{impersonationData.target_user?.email}</span>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
